import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Input,
  IconButton,
  Text,
  Flex,
  useToast,
  Button,
  Tooltip,
} from "@chakra-ui/react";
import { VscSend, VscCopy, VscCheck, VscDiscard, VscFlame } from "react-icons/vsc";
import { AiOutlineRobot } from "react-icons/ai";
import ReactMarkdown from "react-markdown";
import { connect } from "react-redux";
import { chatWithGemini } from "../Utils/aiService";
import { createAnchor, addToGlobalAnchorList } from "./AutoSuggestion";
import aces from "ace-builds";

const Copilot = (props) => {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Hello! I am your LaTeX AI Copilot. I can now suggest specific fixes based on line numbers for maximum precision.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);
  const toast = useToast();
  const editorRef = props.editorRef;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const getEditorContext = () => {
    if (!editorRef?.current?.editor) return null;
    const editor = editorRef.current.editor;
    const session = editor.session;
    const selection = editor.getSelection();
    const cursor = editor.getCursorPosition();
    const range = selection.getRange();

    const selectedText = session.getTextRange(range);
    const cursorLine = cursor.row + 1;
    
    const windowSize = 50;
    const startLine = Math.max(0, cursor.row - windowSize);
    const endLine = Math.min(session.getLength() - 1, cursor.row + windowSize);
    
    const lines = [];
    for (let i = startLine; i <= endLine; i++) {
        lines.push(`${i + 1}: ${session.getLine(i)}`);
    }
    const relevantContext = lines.join("\n");

    return {
      selectedText,
      cursorLine,
      relevantContext
    };
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: "user", content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);

    try {
      const context = getEditorContext();
      const aiResponseContent = await chatWithGemini(newMessages, context);
      
      const suggestions = parseSuggestions(aiResponseContent);
      if (suggestions.length > 0) {
        processAiSuggestions(suggestions);
      }

      const aiMessage = { role: "assistant", content: aiResponseContent };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      toast({
        title: "AI error",
        description: error.message || "Failed to get response from Gemini",
        status: "error",
        duration: 5000,
        position: "bottom-left",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const parseSuggestions = (content) => {
    const jsonRegex = /```json\s*([\s\S]*?)\s*```/g;
    const suggestions = [];
    let match;
    
    while ((match = jsonRegex.exec(content)) !== null) {
      try {
        const parsed = JSON.parse(match[1]);
        if (Array.isArray(parsed)) {
          parsed.forEach(p => { if (p.type === "suggestion") suggestions.push(p); });
        } else if (parsed.type === "suggestion") {
          suggestions.push(parsed);
        }
      } catch (e) {
        console.warn("Failed to parse JSON suggestion block:", e);
      }
    }
    return suggestions;
  };

  const processAiSuggestions = (suggestionList) => {
    if (!editorRef?.current?.editor) return;
    const editor = editorRef.current.editor;
    const session = editor.session;
    const Range = aces.require("ace/range").Range;
    const newItems = [];

    suggestionList.forEach(s => {
      // Use absolute line numbers (1-based from AI)
      const startRow = (parseInt(s.startLine) || 1) - 1;
      const endRow = (parseInt(s.endLine) || startRow + 1) - 1;
      
      if (startRow < 0 || startRow >= session.getLength()) return;

      // Calculate the original text for the diff popup
      const originalLines = [];
      for (let i = startRow; i <= endRow; i++) {
        originalLines.push(session.getLine(i));
      }
      const originalText = originalLines.join("\n");

      // We assume the AI wants to replace the WHOLE lines unless it specified columns (which we didn't ask for yet)
      // For simplicity, we replace full lines.
      const position = {
        startRow: startRow,
        startCol: 0,
        endRow: endRow,
        endCol: session.getLine(endRow).length
      };

      const anchorList = [];
      createAnchor(
        session,
        Range,
        position,
        originalText,
        s.suggestion,
        "ace-ai-suggestion-marker",
        "AI_FIX",
        s.description || "AI Suggestion",
        anchorList,
        { isAI: true }
      );

      if (anchorList.length > 0) {
        const item = anchorList[0];
        item.isAI = true;
        newItems.push(item);
        addToGlobalAnchorList(item);
      }
    });

    if (newItems.length > 0) {
      props.uploadAutoSuggestions(newItems);
      toast({
        title: `${newItems.length} suggestions added`,
        description: "Check the panel or the editor highlights.",
        status: "success",
        duration: 3000,
        position: "top",
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        position: "bottom-left",
    });
  };

  const MessageItem = ({ message }) => {
    const isAssistant = message.role === "assistant";
    return (
      <Flex direction="column" align={isAssistant ? "flex-start" : "flex-end"} mb={4} w="100%">
        <HStack spacing={2} mb={1} align="center" w="100%" justify={isAssistant ? "flex-start" : "flex-end"}>
          {isAssistant && <AiOutlineRobot color="#007fd4" />}
          <Text fontSize="xs" color="gray.500" fontWeight="bold">
            {isAssistant ? "Copilot" : "You"}
          </Text>
        </HStack>
        <Box
          bg={isAssistant ? "#333333" : "#007fd4"}
          color="white"
          p={3}
          borderRadius="lg"
          maxW="95%"
          fontSize="13px"
        >
          <ReactMarkdown
             components={{
                code({ node, inline, className, children, ...props }) {
                  if (!inline && !className?.includes("json")) {
                    return (
                      <Box position="relative" my={2}>
                        <Box as="pre" p={2} bg="#1e1e1e" borderRadius="md" overflowX="auto">
                          <code>{children}</code>
                        </Box>
                      </Box>
                    );
                  }
                  return <code className={className} {...props}>{children}</code>;
                }
              }}
          >
            {message.content}
          </ReactMarkdown>
        </Box>
      </Flex>
    );
  };

  return (
    <Box height="100%" display="flex" flexDirection="column" bg="#252526" overflow="hidden">
      <Box ref={scrollRef} flex={1} w="100%" p={4} overflowY="auto">
        {messages.map((msg, idx) => <MessageItem key={idx} message={msg} />)}
        {isLoading && (
          <Flex align="center" gap={2} mb={4}>
            <AiOutlineRobot color="#007fd4" className="spinning-ai" fontSize="20px" />
            <Text fontSize="xs" color="gray.500">Thinking...</Text>
          </Flex>
        )}
      </Box>

      <Box p={3} w="100%" borderTop="1px solid #333333" bg="#1e1e1e">
        <HStack>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSend()}
            placeholder="Ask Copilot..."
            bg="#252526"
            border="1px solid #3c3c3c"
            fontSize="13px"
            color="white"
          />
          <IconButton
            icon={<VscSend />}
            size="sm"
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isLoading}
            aria-label="Send"
          />
        </HStack>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  uploadAutoSuggestions: (data) => dispatch({ type: "addAutoSuggestion", payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Copilot);
